import type { ReactNode, RefObject } from 'react'
import {
  FieldError,
  Label,
  Text,
  TextField,
  type TextFieldProps,
  type ValidationResult,
} from 'react-aria-components'
import { InputField } from '#ui/Form.tsx'
import type { Size } from '#utils/misc.ts'

export interface FormFieldProps extends TextFieldProps {
  'data-1p-ignore'?: boolean
  description?: string
  errorMessage?: string | ((validation: ValidationResult) => string)
  fieldRef?: RefObject<HTMLInputElement | null>
  isBusy?: boolean
  label?: ReactNode
  placeholder?: string
  size?: Size | undefined
  tabIndex?: number
}

const labelSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
}
const descriptionSizeClasses = {
  sm: 'text-xs',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-sm',
}

export function FormField({
  'data-1p-ignore': data1pIgnore,
  description,
  errorMessage,
  fieldRef,
  isBusy,
  label,
  placeholder,
  size = 'md',
  tabIndex,
  // react-aria props
  isDisabled,
  ...props
}: FormFieldProps) {
  return (
    <TextField {...props} isDisabled={isDisabled || isBusy}>
      {label ? (
        <Label
          className={`tex-base-4 mb-1 block font-medium ${labelSizeClasses[size]}`}
        >
          {label}
        </Label>
      ) : null}
      <InputField
        className='mb-1 w-full'
        data-1p-ignore={data1pIgnore}
        fieldRef={fieldRef}
        placeholder={placeholder}
        rounding='all'
        size={size}
        tabIndex={tabIndex}
        theme='outline'
      />
      <div className='flex justify-between'>
        <FieldError
          className={`block text-flag-4 ${descriptionSizeClasses[size]}`}
        >
          {({ validationDetails, validationErrors }) => {
            return validationDetails.valueMissing
              ? 'Please enter value'
              : typeof errorMessage === 'string'
                ? errorMessage
                : validationErrors.join('. ')
          }}
        </FieldError>
        {description ? (
          <Text
            className={`block ${descriptionSizeClasses[size]}`}
            slot='description'
          >
            {description}
          </Text>
        ) : null}
      </div>
    </TextField>
  )
}
